import {SupportedLocales} from '#types';

const en = {
  title: 'Syncs',
  subtitle: 'Connect data from sources to destinations.',
  newSync: 'New Sync',
  createDestination: 'Create Destination',
  loading: 'Loading',
  tryAgain: 'Try again',
  stats: {syncs: 'Syncs', enabled: 'Enabled', disabled: 'Disabled'},
  table: {
    search: 'Search',
    filterByStatus: 'Filter syncs by status',
    filterByDestination: 'Filter syncs by destination',
    filterByObjectType: 'Filter syncs by object type',
    destinationFilter: 'Destination',
    objectTypeFilter: 'Object Type',
    searchDestinations: 'Search destinations',
    searchObjectTypes: 'Search object types',
    noDestinations: 'There are no destinations to filter with.',
    noObjectTypes: 'There are no object types to filter with.',
    ariaLabel: 'Syncs table',
    enabledStatus: 'Enabled',
    disabledStatus: 'Disabled',
    headers: {
      id: 'ID',
      type: 'Type',
      name: 'Name',
      enabled: 'Enabled',
      dataset: 'Dataset',
      sourceTable: 'Source Table',
      mappedField: 'Mapped Field',
      objectType: 'Object Type',
      createdAt: 'Created At',
    },
    destinationAlt: 'Destination',
    mappingRequired:
      'Add at least one field mapping before enabling this sync.',
    noPermission: 'You do not have permission to update this sync.',
    updateInProgress: 'Another sync update is in progress.',
    cannotEnable: (name: string) =>
      `Cannot enable ${name}: add at least one field mapping`,
    toggle: (name: string) => `Toggle ${name}`,
    loadError: 'Syncs could not be loaded.',
    emptyFiltered: 'There are no syncs matching your filters.',
    empty: 'There are no syncs.',
  },
  status: {
    changed: (enabled: boolean) =>
      `Sync has been ${enabled ? 'enabled' : 'disabled'}`,
    updateError: 'Unable to update the sync.',
    updateFailed: 'Update failed',
    enableTitle: 'Enable sync?',
    disableTitle: 'Disable sync?',
    confirm: (enabled: boolean, name: string) =>
      `Are you sure you want to ${enabled ? 'enable' : 'disable'} “${name}”?`,
    enable: 'Enable',
    disable: 'Disable',
  },
  form: {
    settings: 'Sync settings',
    name: 'Name',
    nameRequired: 'Name is required',
    frequency: 'Frequency',
    selectFrequency: 'Select a frequency',
    frequencyRequired: 'Frequency is required',
    updateRule: 'Update rule',
    selectUpdateRule: 'Select an update rule',
    updateRuleRequired: 'Update rule is required',
    enabled: 'Enabled',
    mappingBeforeEnable:
      'Add at least one field mapping before enabling this sync.',
    source: 'Source',
    destination: 'Destination',
    dataset: 'Dataset / table',
    table: 'Table',
    selectDataset: 'Select a dataset',
    searchDatasets: 'Search datasets',
    datasetRequired: 'Dataset is required',
    uniqueFields: 'Dataset unique field(s)',
    matchField: 'Match field',
    selectField: 'Select a field',
    loadingFields: 'Loading fields…',
    searchSourceFields: 'Search source fields',
    sourceMatchRequired: 'Source match field is required',
    connection: 'Connection',
    selectConnection: 'Select a connection',
    searchConnections: 'Search connections',
    destinationRequired: 'Destination connection is required',
    reauthenticationRequired:
      'This connection must be reauthenticated before it can be used.',
    reauthenticate: 'Reauthenticate connection',
    objectType: 'Object type',
    selectObject: 'Select an object',
    loadingObjects: 'Loading objects…',
    searchObjects: 'Search objects',
    objectRequired: 'Destination object is required',
    searchDestinationFields: 'Search destination fields',
    destinationMatchRequired: 'Destination match field is required',
    detailsTabs: 'Sync details',
    mappingsTab: 'Field mapping',
    activityTab: 'Activity',
    object: 'Object',
    cancel: 'Cancel',
    create: 'Create sync',
    save: 'Save changes',
    optionsError: 'Unable to load the available sync settings.',
    saveError: 'Unable to save this sync.',
    updateError: 'Unable to update the sync.',
    updateFailed: 'Update failed',
    loadingAria: 'Loading sync',
  },
  mapping: {
    title: 'Field mapping',
    description:
      'Choose which source fields are written to destination fields. You can also enter a custom destination field name.',
    required: (fields: string) => `Required: ${fields}. `,
    recommended: (fields: string) => `Recommended: ${fields}.`,
    sourceLoadError: 'Unable to load source fields.',
    destinationLoadError: 'Unable to load destination fields.',
    validationError: 'Unable to validate field mappings.',
    loadingError: 'Field options are still loading.',
    missingRequired: (fields: string) =>
      `Map all required destination fields: ${fields}.`,
    invalid: 'The field mapping is invalid.',
    sourceField: 'Source field',
    destinationField: 'Destination field',
    empty: 'No field mappings configured.',
    loadingSource: 'Loading source fields…',
    selectSource: 'Select a source field',
    sourceRequired: 'Source field is required',
    searchSource: 'Search source fields',
    loadingDestination: 'Loading destination fields…',
    selectDestination: 'Select or enter a destination field',
    destinationRequired: 'Destination field is required',
    searchDestination: 'Search or enter a destination field',
    remove: (index: number) => `Remove mapping ${index}`,
    add: 'Add field',
  },
  activity: {
    filterByStatus: 'Filter sync activity by status',
    allExceptEmpty: 'All except Empty',
    allStatuses: 'All statuses',
    status: 'Status',
    exportTime: 'Export time',
    successfulRecords: 'Successful records',
    rejectedRecords: 'Rejected records',
    exportId: 'Export ID',
    copyExportId: (exportId: string) => `Copy export ID ${exportId}`,
    exportIdCopied: 'Export ID copied',
    exportIdCopyError: 'Unable to copy export ID.',
    empty: 'No sync activity found',
    loadError: 'Sync activity could not be loaded.',
  },
  actions: {
    ariaLabel: 'Sync actions',
    failed: 'Action failed',
    run: 'Run',
    runStarted: 'Sync run started',
    runError: 'Unable to run the sync.',
    enableBeforeRun: 'Enable this sync before running it',
    copy: 'Copy',
    copied: 'Sync copied',
    copyError: 'Unable to copy the sync.',
    delete: 'Delete',
    disableBeforeDelete: 'Disable this sync before deleting it',
    deleted: 'Sync deleted',
    deleteError: 'Unable to delete the sync.',
    deleteTitle: 'Delete sync?',
    deleteDescription: (name: string) =>
      `Are you sure you want to delete “${name}”?`,
    deleteConfirm: 'Delete sync',
  },
  backfill: {
    title: 'Update sync',
    description: 'Choose how newly mapped fields should be applied.',
    updatesOnly: 'Updates only (recommended)',
    updatesOnlyDescription:
      'Send an update only when the value of a mapped field changes.',
    updatesOnlyExample:
      'Example: If “user_location” is newly mapped, it will be sent after a customer’s location changes.',
    backfill: 'Back-fill existing customers (advanced)',
    backfillDescription:
      'Send newly mapped field values for all existing customers to the destination.',
    backfillExample:
      'Example: If “user_location” is newly mapped, send its current value for every existing customer.',
    acknowledgement:
      'I understand the time and cost implications of back-filling sync data, especially for a large customer base.',
    confirm: 'Update sync',
  },
  pages: {
    created: 'Sync created',
    updated: 'Sync updated',
    settingsLoadError: 'Sync settings could not be loaded.',
    notFound: 'Sync was not found.',
    loadError: 'Sync could not be loaded.',
  },
  frequencyLabels: {
    fifteen_minutes: 'Fifteen minutes',
    thirty_minutes: 'Thirty minutes',
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    realtime: 'Realtime',
  },
  updateRuleLabels: {
    update_only: 'Update only',
    insert_only: 'Insert only',
    insert_or_update: 'Insert or update',
  },
};

export const signalRoutesLang = {
  [SupportedLocales.EN]: en,
  [SupportedLocales.ES]: {
    ...en,
    title: 'Sincronizaciones',
    subtitle: 'Conecta datos de orígenes con destinos.',
    newSync: 'Nueva sincronización',
    createDestination: 'Crear destino',
    loading: 'Cargando',
    tryAgain: 'Intentar de nuevo',
    stats: {
      syncs: 'Sincronizaciones',
      enabled: 'Habilitadas',
      disabled: 'Deshabilitadas',
    },
    table: {
      ...en.table,
      search: 'Buscar',
      filterByStatus: 'Filtrar sincronizaciones por estado',
      filterByDestination: 'Filtrar sincronizaciones por destino',
      filterByObjectType: 'Filtrar sincronizaciones por tipo de objeto',
      destinationFilter: 'Destino',
      objectTypeFilter: 'Tipo de objeto',
      searchDestinations: 'Buscar destinos',
      searchObjectTypes: 'Buscar tipos de objeto',
      noDestinations: 'No hay destinos disponibles para filtrar.',
      noObjectTypes: 'No hay tipos de objeto disponibles para filtrar.',
      ariaLabel: 'Tabla de sincronizaciones',
      enabledStatus: 'Habilitada',
      disabledStatus: 'Deshabilitada',
      headers: {
        id: 'ID',
        type: 'Tipo',
        name: 'Nombre',
        enabled: 'Habilitada',
        dataset: 'Conjunto de datos',
        sourceTable: 'Tabla de origen',
        mappedField: 'Campo asignado',
        objectType: 'Tipo de objeto',
        createdAt: 'Fecha de creación',
      },
      destinationAlt: 'Destino',
      mappingRequired:
        'Agrega al menos una asignación de campo antes de habilitar esta sincronización.',
      noPermission: 'No tienes permiso para actualizar esta sincronización.',
      updateInProgress: 'Hay otra actualización de sincronización en curso.',
      cannotEnable: (name: string) =>
        `No se puede habilitar ${name}: agrega al menos una asignación de campo`,
      toggle: (name: string) => `Cambiar estado de ${name}`,
      loadError: 'No se pudieron cargar las sincronizaciones.',
      emptyFiltered: 'No hay sincronizaciones que coincidan con tus filtros.',
      empty: 'No hay sincronizaciones.',
    },
    status: {
      changed: (enabled: boolean) =>
        `La sincronización se ha ${enabled ? 'habilitado' : 'deshabilitado'}`,
      updateError: 'No se pudo actualizar la sincronización.',
      updateFailed: 'Error de actualización',
      enableTitle: '¿Habilitar sincronización?',
      disableTitle: '¿Deshabilitar sincronización?',
      confirm: (enabled: boolean, name: string) =>
        `¿Seguro que quieres ${enabled ? 'habilitar' : 'deshabilitar'} “${name}”?`,
      enable: 'Habilitar',
      disable: 'Deshabilitar',
    },
    form: {
      ...en.form,
      settings: 'Configuración de sincronización',
      name: 'Nombre',
      nameRequired: 'El nombre es obligatorio',
      frequency: 'Frecuencia',
      selectFrequency: 'Selecciona una frecuencia',
      frequencyRequired: 'La frecuencia es obligatoria',
      updateRule: 'Regla de actualización',
      selectUpdateRule: 'Selecciona una regla de actualización',
      updateRuleRequired: 'La regla de actualización es obligatoria',
      enabled: 'Habilitada',
      mappingBeforeEnable:
        'Agrega al menos una asignación de campo antes de habilitar esta sincronización.',
      source: 'Origen',
      destination: 'Destino',
      dataset: 'Conjunto de datos / tabla',
      table: 'Tabla',
      selectDataset: 'Selecciona un conjunto de datos',
      searchDatasets: 'Buscar conjuntos de datos',
      datasetRequired: 'El conjunto de datos es obligatorio',
      uniqueFields: 'Campos únicos del conjunto de datos',
      matchField: 'Campo de coincidencia',
      selectField: 'Selecciona un campo',
      loadingFields: 'Cargando campos…',
      searchSourceFields: 'Buscar campos de origen',
      sourceMatchRequired: 'El campo de coincidencia de origen es obligatorio',
      connection: 'Conexión',
      selectConnection: 'Selecciona una conexión',
      searchConnections: 'Buscar conexiones',
      destinationRequired: 'La conexión de destino es obligatoria',
      reauthenticationRequired:
        'Esta conexión debe volver a autenticarse antes de poder usarse.',
      reauthenticate: 'Volver a autenticar la conexión',
      objectType: 'Tipo de objeto',
      selectObject: 'Selecciona un objeto',
      loadingObjects: 'Cargando objetos…',
      searchObjects: 'Buscar objetos',
      objectRequired: 'El objeto de destino es obligatorio',
      searchDestinationFields: 'Buscar campos de destino',
      destinationMatchRequired:
        'El campo de coincidencia de destino es obligatorio',
      object: 'Objeto',
      cancel: 'Cancelar',
      create: 'Crear sincronización',
      save: 'Guardar cambios',
      optionsError:
        'No se pudo cargar la configuración de sincronización disponible.',
      saveError: 'No se pudo guardar esta sincronización.',
      updateError: 'No se pudo actualizar la sincronización.',
      updateFailed: 'Error de actualización',
      loadingAria: 'Cargando sincronización',
    },
    mapping: {
      ...en.mapping,
      title: 'Asignación de campos',
      description:
        'Elige qué campos de origen se escriben en los campos de destino. También puedes introducir un nombre de campo de destino personalizado.',
      required: (fields: string) => `Obligatorios: ${fields}. `,
      recommended: (fields: string) => `Recomendados: ${fields}.`,
      sourceLoadError: 'No se pudieron cargar los campos de origen.',
      destinationLoadError: 'No se pudieron cargar los campos de destino.',
      validationError: 'No se pudieron validar las asignaciones de campos.',
      loadingError: 'Las opciones de campo todavía se están cargando.',
      missingRequired: (fields: string) =>
        `Asigna todos los campos de destino obligatorios: ${fields}.`,
      invalid: 'La asignación de campos no es válida.',
      sourceField: 'Campo de origen',
      destinationField: 'Campo de destino',
      empty: 'No hay asignaciones de campos configuradas.',
      loadingSource: 'Cargando campos de origen…',
      selectSource: 'Selecciona un campo de origen',
      sourceRequired: 'El campo de origen es obligatorio',
      searchSource: 'Buscar campos de origen',
      loadingDestination: 'Cargando campos de destino…',
      selectDestination: 'Selecciona o introduce un campo de destino',
      destinationRequired: 'El campo de destino es obligatorio',
      searchDestination: 'Busca o introduce un campo de destino',
      remove: (index: number) => `Eliminar asignación ${index}`,
      add: 'Agregar campo',
    },
    activity: {
      filterByStatus: 'Filtrar actividad de sincronización por estado',
      allExceptEmpty: 'Todos excepto vacíos',
      allStatuses: 'Todos los estados',
      status: 'Estado',
      exportTime: 'Hora de exportación',
      successfulRecords: 'Registros correctos',
      rejectedRecords: 'Registros rechazados',
      exportId: 'ID de exportación',
      copyExportId: (exportId: string) =>
        `Copiar ID de exportación ${exportId}`,
      exportIdCopied: 'ID de exportación copiado',
      exportIdCopyError: 'No se pudo copiar el ID de exportación.',
      empty: 'No se encontró actividad de sincronización',
      loadError: 'No se pudo cargar la actividad de sincronización.',
    },
    actions: {
      ...en.actions,
      ariaLabel: 'Acciones de sincronización',
      failed: 'Error de acción',
      run: 'Ejecutar',
      runStarted: 'Se inició la sincronización',
      runError: 'No se pudo ejecutar la sincronización.',
      enableBeforeRun: 'Habilita esta sincronización antes de ejecutarla',
      copy: 'Copiar',
      copied: 'Sincronización copiada',
      copyError: 'No se pudo copiar la sincronización.',
      delete: 'Eliminar',
      disableBeforeDelete:
        'Deshabilita esta sincronización antes de eliminarla',
      deleted: 'Sincronización eliminada',
      deleteError: 'No se pudo eliminar la sincronización.',
      deleteTitle: '¿Eliminar sincronización?',
      deleteDescription: (name: string) =>
        `¿Seguro que quieres eliminar “${name}”?`,
      deleteConfirm: 'Eliminar sincronización',
    },
    backfill: {
      title: 'Actualizar sincronización',
      description: 'Elige cómo se deben aplicar los campos recién asignados.',
      updatesOnly: 'Solo actualizaciones (recomendado)',
      updatesOnlyDescription:
        'Envía una actualización solo cuando cambie el valor de un campo asignado.',
      updatesOnlyExample:
        'Ejemplo: Si “user_location” se acaba de asignar, se enviará cuando cambie la ubicación de un cliente.',
      backfill: 'Completar clientes existentes (avanzado)',
      backfillDescription:
        'Envía los valores de los campos recién asignados de todos los clientes existentes al destino.',
      backfillExample:
        'Ejemplo: Si “user_location” se acaba de asignar, envía su valor actual para cada cliente existente.',
      acknowledgement:
        'Entiendo las implicaciones de tiempo y costo de completar los datos de la sincronización, especialmente para una base de clientes grande.',
      confirm: 'Actualizar sincronización',
    },
    pages: {
      created: 'Sincronización creada',
      updated: 'Sincronización actualizada',
      settingsLoadError:
        'No se pudo cargar la configuración de sincronización.',
      notFound: 'No se encontró la sincronización.',
      loadError: 'No se pudo cargar la sincronización.',
    },
    frequencyLabels: {
      fifteen_minutes: 'Quince minutos',
      thirty_minutes: 'Treinta minutos',
      hourly: 'Cada hora',
      daily: 'Diariamente',
      weekly: 'Semanalmente',
      monthly: 'Mensualmente',
      realtime: 'En tiempo real',
    },
    updateRuleLabels: {
      update_only: 'Solo actualizar',
      insert_only: 'Solo insertar',
      insert_or_update: 'Insertar o actualizar',
    },
  },
};
